import { useCallback, useEffect, useState } from "react";
import { CredentialWithId } from "../../../../pages/credentialsConfigration/types/credentialTypes";
import { MfaMethod } from "../types";

interface UseMfaBranchProps {
  isOpen: boolean;
  isEditMode: boolean;
  credentialData: CredentialWithId | null;
  handleInputChange: (field: string, value: string) => void;
  markTouched: (field: string) => void;
  clearTouched: (field: string) => void;
}

/**
 * Owns the active MFA branch (`secret_key` vs `otp_email`) and the
 * branch-switch handler that keeps form data and touched flags in sync.
 *
 * Seeding rules on modal open:
 * - Edit mode with a credential carrying an `email` -> `otp_email` branch.
 * - Otherwise -> `secret_key` branch (default for add mode and for edit
 *   mode credentials that already use a TOTP secret).
 *
 * Branch-switch side effects (`handleMfaMethodChange`):
 * - Clears the now-irrelevant field (`email` or `secret_key`) on the form
 *   so the save payload contains only the chosen method's value.
 * - Clears the touched flag of the abandoned field so its stale error
 *   doesn't linger after switching branches.
 * - Marks the newly active required field as touched so its "required"
 *   error surfaces immediately if it's blank.
 */
export const useMfaBranch = ({
  isOpen,
  isEditMode,
  credentialData,
  handleInputChange,
  markTouched,
  clearTouched,
}: UseMfaBranchProps) => {
  const [mfaMethod, setMfaMethod] = useState<MfaMethod>("secret_key");

  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode && credentialData) {
      setMfaMethod(credentialData.email ? "otp_email" : "secret_key");
    } else {
      setMfaMethod("secret_key");
    }
  }, [isOpen, isEditMode, credentialData]);

  const handleMfaMethodChange = useCallback(
    (value: string) => {
      const next: MfaMethod =
        value === "otp_email" ? "otp_email" : "secret_key";
      setMfaMethod(next);
      if (next === "secret_key") {
        handleInputChange("email", "");
        clearTouched("email");
        markTouched("secret_key");
      } else {
        handleInputChange("secret_key", "");
        clearTouched("secret_key");
        markTouched("email");
      }
    },
    [handleInputChange, clearTouched, markTouched],
  );

  return { mfaMethod, handleMfaMethodChange };
};
